import { Box, Button, Tooltip, useTheme } from "@mui/material";
import { credentialVerificationUnavailableReason } from "encamp-shared/src/portal/portalCanVerifyCredentials";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityStatus, CredentialVerificationStatus } from "generated-graphql/graphql";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useVerifyCredential } from "hooks/useVerifyCredentials";
import { relativeLogTime } from "util/dates";

import { FieldDisplay } from "./shared/FieldComponents";
import { useReportDetails } from "./useReportDetails";

const RELATIVE_TIME = 12;
const NEVER_CHECKED = "Never Checked";

function isoTimestamp(value: string | null | undefined): string | undefined {
  return value == null ? undefined : String(value);
}

/** Fields the Verify control reads. Shared by customer, staff, and fulfillment. */
export type CredentialVerificationFields = {
  id?: string | null;
  verificationStatus?: CredentialVerificationStatus | null;
  lastVerificationCheckAt?: string | null;
  lastVerificationSuccessAt?: string | null;
};

export const CredentialVerification = ({
  credential,
  facilityId,
  portal,
  disabled = false,
  children,
  actionAlign = "flex-end",
}: {
  credential: CredentialVerificationFields | null | undefined;
  facilityId?: string;
  portal: { verifiable: boolean; isActive: boolean } | null | undefined;
  disabled?: boolean;
  children?: React.ReactNode;
  /**
   * Where the Verify button sits. The credential dialog lines it up with the field
   * column it sits under; the fulfillment page keeps it to the right of its panel.
   */
  actionAlign?: "flex-start" | "flex-end";
}) => {
  const theme = useTheme();
  const unavailableReason = credentialVerificationUnavailableReason(portal);
  const { data } = useReportDetails();
  const [verifyCredential, { loading: sendingCredentialVerification }] = useVerifyCredential();
  const [createComment] = useCreateTierIIReportComment();

  // Get current activity and check if it's in error
  const currentActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (act) => act.id === data?.tierIIReport.currentWorkflow?.currentActivityId
  );
  const isActivityInError = currentActivity?.status === ActivityStatus.Error;

  // Get error message from activity logs if activity is in error
  const { data: logData } = useActivityLogs(
    currentActivity?.id,
    isActivityInError ? `status:${ActivityStatus.Error}` : "",
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const errorMessage =
    isActivityInError && logData?.activityLogs.items?.[0]?.message
      ? logData.activityLogs.items[0].message
      : null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;

  const handleVerifyCredential = async () => {
    await verifyCredential({
      variables: {
        input: {
          facilityId,
          credentialId: credential?.id,
        },
      },
      refetchQueries: ["GetTierIIReport", "GetCredential", "CredentialForStaff"],
    });

    // Add workflow comment
    if (workflowId && reportId) {
      let commentText = "Verified credential";
      if (errorMessage) {
        commentText += `:\n"${errorMessage}"`;
      }

      await createComment({
        variables: {
          input: {
            workflowId,
            reportId,
            comment: commentText,
          },
        },
      });
    }
  };

  const verificationTime = isoTimestamp(credential?.lastVerificationCheckAt);
  const lastSuccessfulVerificationTime = isoTimestamp(credential?.lastVerificationSuccessAt);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}>
      <FieldDisplay
        label="Verification Status:"
        value={prettyPrintEnumValue(credential?.verificationStatus)}
        noWrap
      />
      <FieldDisplay
        label="Last Check:"
        value={
          <Tooltip title={verificationTime}>
            <span>
              {verificationTime ? relativeLogTime(verificationTime, RELATIVE_TIME) : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        noWrap
      />
      <FieldDisplay
        label={
          <>
            Last Successful
            <br />
            Verification Check:
          </>
        }
        value={
          <Tooltip title={lastSuccessfulVerificationTime}>
            <span>
              {lastSuccessfulVerificationTime
                ? relativeLogTime(lastSuccessfulVerificationTime, RELATIVE_TIME)
                : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        alignItems="flex-start"
        noWrap
      />
      {children}
      <Box paddingTop={theme.spacing(1)} sx={{ display: "flex", justifyContent: actionAlign }}>
        <Tooltip title={unavailableReason ?? ""}>
          <span>
            <Button
              disabled={
                disabled ||
                !!unavailableReason ||
                credential?.verificationStatus ===
                  CredentialVerificationStatus.CurrentlyVerifying ||
                !!sendingCredentialVerification
              }
              variant="outlined"
              onClick={handleVerifyCredential}
              size="small"
            >
              Verify Credential
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
